/**
 * Multi-camera rendering stack.
 * Cameras render in priority order (lower first). Each camera can have its own
 * viewport, layer mask, and clear settings.
 */

import type * as THREE from 'three/webgpu';

export interface CameraEntry {
  camera: THREE.Camera;
  priority: number;
  viewport?: { x: number; y: number; width: number; height: number };
  layers?: number;
  clearColor?: boolean;
  clearDepth?: boolean;
  clearStencil?: boolean;
  enabled?: boolean;
}

export class CameraStack {
  private entries: CameraEntry[] = [];
  private sorted = false;
  private _main: THREE.Camera | null = null;

  add(entry: CameraEntry): void {
    if (entry.enabled === undefined) entry.enabled = true;
    if (entry.clearColor === undefined) entry.clearColor = true;
    if (entry.clearDepth === undefined) entry.clearDepth = true;
    this.entries.push(entry);
    this.sorted = false;

    // _main should always be the camera with the lowest priority value
    if (this._main === null) {
      this._main = entry.camera;
    } else {
      const currentMainEntry = this.entries.find(e => e.camera === this._main);
      if (!currentMainEntry || entry.priority < currentMainEntry.priority) {
        this._main = entry.camera;
      }
    }
  }

  remove(camera: THREE.Camera): void {
    const idx = this.entries.findIndex(e => e.camera === camera);
    if (idx !== -1) {
      this.entries.splice(idx, 1);
      this.sorted = false;
      if (this._main === camera) {
        // Pick the highest-priority (lowest value) remaining camera
        if (this.entries.length > 0) {
          const sorted = this.getEntries(); // sorts internally
          this._main = sorted[0].camera;
        } else {
          this._main = null;
        }
      }
    }
  }

  get main(): THREE.Camera {
    if (!this._main) throw new Error('No cameras in stack');
    return this._main;
  }

  set main(camera: THREE.Camera) {
    this._main = camera;
    // Ensure it's in the stack
    if (!this.entries.some(e => e.camera === camera)) {
      this.add({ camera, priority: 0 });
    }
  }

  getEntries(): readonly CameraEntry[] {
    if (!this.sorted) {
      this.entries.sort((a, b) => a.priority - b.priority);
      this.sorted = true;
    }
    return this.entries;
  }
}
